import React, { useState, useCallback, useEffect } from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Modal as UIModal } from "@/components/ui/Modal";
import { AppTextArea } from "@/components/ui/AppTextArea";
import type { RejectFieldMeta } from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

export interface RejectFieldModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (reason: string, note: string) => void;
  fieldMeta?: RejectFieldMeta;
  /** Pre-fill the reason field (e.g. when editing an existing rejection). */
  initialReason?: string;
  /** Pre-fill the internal note field (e.g. when editing an existing rejection). */
  initialNote?: string;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function RejectFieldModal({
  visible,
  onDismiss,
  onConfirm,
  fieldMeta,
  initialReason,
  initialNote,
  applicationId,
  t,
}: RejectFieldModalProps) {
  const [reason, setReason] = useState(initialReason ?? "");
  const [note, setNote] = useState(initialNote ?? "");

  const resetForm = useCallback(() => {
    setReason("");
    setNote("");
  }, []);

  const handleDismiss = useCallback(() => {
    resetForm();
    onDismiss();
  }, [resetForm, onDismiss]);

  // Pre-fill when the modal re-opens with initial values (e.g. editing an existing rejection)
  useEffect(() => {
    if (visible) {
      setReason(initialReason ?? "");
      setNote(initialNote ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleConfirm = useCallback(() => {
    const r = reason;
    const n = note;
    resetForm();
    onConfirm(r, n);
  }, [reason, note, resetForm, onConfirm]);

  return (
    <UIModal
      visible={visible}
      onDismiss={handleDismiss}
      title="review.review.decision.rejectFieldModal.title"
      closeOnBackdrop={false}
      useScrollView
      footer={
        <View className="flex-row items-center justify-end gap-4">
          <Button label="common.cancel" variant="tertiary" onPress={handleDismiss} />
          <Button
            label="review.application.reject"
            variant="destructive"
            disabled={!reason.trim()}
            onPress={handleConfirm}
          />
        </View>
      }
    >
      {/* REJECTED FIELD banner */}
      {fieldMeta && (
        <View className="w-full bg-error-light border border-error-border rounded-lg px-6 py-5 mb-6 gap-2 items-start">
          <AppText weight="semibold" className="text-sm text-error tracking-[0.56px]">
            {t("review.review.decision.rejectFieldModal.rejectedFieldLabel")}
          </AppText>
          <AppText className="text-sm text-supporting-text-40">{fieldMeta.label}</AppText>
          {fieldMeta.value && (
            <AppText weight="medium" className="text-base text-dark-text-50">
              {fieldMeta.value}
            </AppText>
          )}
          {fieldMeta.fileKey && (
            <FilePreview
              fileKey={fieldMeta.fileKey}
              isVendor={false}
              applicationId={applicationId}
            />
          )}
        </View>
      )}

      {/* Reason for Rejection (required) */}
      <AppTextArea
        label="review.review.decision.rejectModal.reasonForRejection"
        placeholder="review.review.decision.rejectFieldModal.reasonPlaceholder"
        value={reason}
        onChangeText={setReason}
        numberOfLines={4}
        required
        containerClassName="mb-6"
      />

      {/* Internal Note (optional) */}
      <AppTextArea
        label="review.review.decision.rejectFieldModal.internalNote"
        placeholder="review.review.decision.rejectFieldModal.internalNotePlaceholder"
        value={note}
        onChangeText={setNote}
        numberOfLines={4}
      />
    </UIModal>
  );
}
